/**
 * Admin Dashboard — metrics, users table, comp granting, impersonation, Stripe events.
 */
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/components/ConfirmDialog";
import { AppHeader } from "@/components/Header";
import { toast } from "sonner";
import { Users, Activity, FileText, DollarSign, Gift, UserCheck, RefreshCw } from "lucide-react";

export default function AdminDashboard() {
  const { user, impersonate } = useAuth();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [users, setUsers] = useState([]);
  const [events, setEvents] = useState([]);
  const [impLog, setImpLog] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [compModal, setCompModal] = useState(null); // {user, reason}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, u, e, l] = await Promise.all([
        api.get("/admin/metrics"),
        api.get("/admin/users"),
        api.get("/admin/stripe-events"),
        api.get("/admin/impersonation-log"),
      ]);
      setMetrics(m.data);
      setUsers(u.data.users || []);
      setEvents(e.data.events || []);
      setImpLog(l.data.events || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load admin data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const doImpersonate = async (target) => {
    const ok = await confirm({
      title: `Sign in as ${target.email}?`,
      description: "You'll be logged into their account in a read-and-write impersonation session. This action is logged.",
      confirmLabel: "Impersonate",
    });
    if (!ok) return;
    const res = await impersonate(target.id);
    if (res.ok) {
      navigate("/app");
    } else {
      toast.error(res.error);
    }
  };

  const submitComp = async (granted) => {
    if (!compModal) return;
    try {
      await api.post(`/admin/users/${compModal.user.id}/comp`, {
        granted,
        reason: compModal.reason || "",
      });
      toast.success(granted ? "Comp granted" : "Comp revoked");
      setCompModal(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const filteredUsers = users.filter((u) => {
    const q = filter.trim().toLowerCase();
    if (!q) return true;
    return (
      (u.email || "").toLowerCase().includes(q) ||
      (u.name || "").toLowerCase().includes(q) ||
      (u.subscription_status || "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="min-h-screen bg-[var(--dash-bg)]">
      <AppHeader />

      <div className="max-w-[1400px] mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="text-[12px] uppercase tracking-[0.22em] text-[var(--ink-muted)] mb-1">Operations</p>
            <h1 className="font-display text-4xl tracking-tight" data-testid="admin-title">Admin</h1>
          </div>
          <button onClick={load} className="btn-ghost text-sm inline-flex items-center gap-2" data-testid="admin-refresh-btn">
            <RefreshCw size={14} strokeWidth={1.5} /> Refresh
          </button>
        </div>

        {/* Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <Metric icon={Users} label="Total writers" value={metrics?.users?.total} loading={!metrics} testId="metric-total" />
          <Metric icon={Activity} label="Active subs" value={metrics?.users?.active} loading={!metrics} testId="metric-active" />
          <Metric icon={DollarSign} label="MRR" value={metrics ? `$${((metrics.mrr_cents || 0) / 100).toFixed(2)}` : undefined} loading={!metrics} testId="metric-mrr" />
          <Metric icon={FileText} label="Scripts created" value={metrics?.scripts_total} loading={!metrics} testId="metric-scripts" />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <Metric label="On trial" value={metrics?.users?.trialing} loading={!metrics} small testId="metric-trialing" />
          <Metric label="Past due" value={metrics?.users?.past_due} loading={!metrics} small testId="metric-pastdue" />
          <Metric label="Locked" value={metrics?.users?.locked} loading={!metrics} small testId="metric-locked" />
          <Metric label="Comped" value={metrics?.users?.comped} loading={!metrics} small testId="metric-comped" />
        </div>

        {/* Users table */}
        <section className="bg-white border border-[var(--line)] rounded-md mb-10 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--line)]">
            <h2 className="font-display text-xl">Writers</h2>
            <input
              placeholder="Filter by email, name, status…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              data-testid="admin-users-filter"
              className="text-sm bg-transparent border-b border-[var(--line)] focus:border-[var(--brand)] outline-none py-1 px-2 w-72"
            />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-users-table">
              <thead className="text-left text-[10px] uppercase tracking-widest text-[var(--ink-muted)] border-b border-[var(--line)]">
                <tr>
                  <th className="px-5 py-3">Email</th>
                  <th className="px-5 py-3">Name</th>
                  <th className="px-5 py-3">Role</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3">Trial ends</th>
                  <th className="px-5 py-3">Joined</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="text-[var(--ink-primary)]">
                {loading ? (
                  <tr><td colSpan={7} className="px-5 py-6 text-[var(--ink-muted)]">Loading…</td></tr>
                ) : filteredUsers.length === 0 ? (
                  <tr><td colSpan={7} className="px-5 py-6 text-[var(--ink-muted)]">No writers match that filter.</td></tr>
                ) : filteredUsers.map((u) => (
                  <tr key={u.id} className="border-b border-[var(--line)] last:border-0 hover:bg-[var(--canvas)]/60" data-testid={`admin-user-row-${u.id}`}>
                    <td className="px-5 py-3 font-mono-script text-[12px]" data-testid={`admin-user-email-${u.id}`}>{u.email}</td>
                    <td className="px-5 py-3">{u.name || "—"}</td>
                    <td className="px-5 py-3 text-xs uppercase tracking-wider text-[var(--ink-secondary)]">{u.role}</td>
                    <td className="px-5 py-3"><StatusPill status={u.subscription_status} comp={u.comp_granted} /></td>
                    <td className="px-5 py-3 text-xs text-[var(--ink-secondary)]">{formatDateShort(u.trial_ends_at)}</td>
                    <td className="px-5 py-3 text-xs text-[var(--ink-secondary)]">{formatDateShort(u.created_at)}</td>
                    <td className="px-5 py-3 text-right whitespace-nowrap">
                      {u.id !== user.id && (
                        <>
                          <button
                            onClick={() => doImpersonate(u)}
                            className="text-xs underline-offset-2 hover:underline mr-3 text-[var(--ink-secondary)]"
                            data-testid={`admin-impersonate-${u.id}`}
                          >
                            <UserCheck size={12} strokeWidth={1.5} className="inline mr-1" />
                            Sign in as
                          </button>
                          <button
                            onClick={() => setCompModal({ user: u, reason: "" })}
                            className="text-xs underline-offset-2 hover:underline text-[var(--brand)]"
                            data-testid={`admin-comp-${u.id}`}
                          >
                            <Gift size={12} strokeWidth={1.5} className="inline mr-1" />
                            {u.comp_granted ? "Revoke comp" : "Comp"}
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Activity */}
        <div className="grid md:grid-cols-2 gap-6">
          <section className="bg-white border border-[var(--line)] rounded-md overflow-hidden">
            <div className="px-5 py-4 border-b border-[var(--line)]"><h2 className="font-display text-xl">Recent Stripe events</h2></div>
            <ul className="max-h-[400px] overflow-y-auto divide-y divide-[var(--line)]" data-testid="admin-stripe-events">
              {events.length === 0 && <li className="px-5 py-4 text-sm text-[var(--ink-muted)]">No events yet.</li>}
              {events.map((e, i) => (
                <li key={i} className="px-5 py-3 text-xs flex justify-between gap-3" data-testid={`stripe-event-${i}`}>
                  <span className="font-mono-script text-[11px] text-[var(--ink-primary)]">{e.type}</span>
                  <span className="text-[var(--ink-muted)] whitespace-nowrap">{formatDateShort(e.received_at)}</span>
                </li>
              ))}
            </ul>
          </section>
          <section className="bg-white border border-[var(--line)] rounded-md overflow-hidden">
            <div className="px-5 py-4 border-b border-[var(--line)]"><h2 className="font-display text-xl">Impersonation log</h2></div>
            <ul className="max-h-[400px] overflow-y-auto divide-y divide-[var(--line)]" data-testid="admin-impersonation-log">
              {impLog.length === 0 && <li className="px-5 py-4 text-sm text-[var(--ink-muted)]">Nothing logged yet.</li>}
              {impLog.map((e, i) => (
                <li key={i} className="px-5 py-3 text-xs" data-testid={`imp-log-${i}`}>
                  <div className="text-[var(--ink-primary)]"><strong>{e.admin_email}</strong> → {e.target_email}</div>
                  <div className="text-[var(--ink-muted)] mt-0.5">{formatDateShort(e.started_at)}</div>
                </li>
              ))}
            </ul>
          </section>
        </div>
      </div>

      {/* Comp modal */}
      {compModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" data-testid="comp-modal">
          <div className="bg-white border border-[var(--line)] rounded-md p-6 max-w-md w-full">
            <h3 className="font-display text-2xl mb-1">
              {compModal.user.comp_granted ? "Revoke comp access" : "Grant comp access"}
            </h3>
            <p className="text-sm text-[var(--ink-secondary)] mb-4">For <strong>{compModal.user.email}</strong></p>
            <textarea
              value={compModal.reason}
              onChange={(e) => setCompModal({ ...compModal, reason: e.target.value })}
              placeholder="Reason (e.g., test user, friend of the show, beta tester)"
              data-testid="comp-reason-input"
              className="w-full border border-[var(--line)] rounded p-3 text-sm min-h-[80px] resize-none focus:border-[var(--brand)] outline-none mb-4"
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setCompModal(null)} className="btn-ghost text-sm" data-testid="comp-cancel-btn">Cancel</button>
              <button
                onClick={() => submitComp(!compModal.user.comp_granted)}
                className="btn-brand text-sm"
                data-testid="comp-submit-btn"
              >
                {compModal.user.comp_granted ? "Revoke" : "Grant"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Metric({ icon: Icon, label, value, small, loading, testId }) {
  return (
    <div className={`bg-white border border-[var(--line)] rounded-md ${small ? "p-4" : "p-5"}`} data-testid={testId}>
      <div className="flex items-center justify-between mb-1.5">
        <p className="text-[10px] uppercase tracking-widest text-[var(--ink-muted)]">{label}</p>
        {Icon && <Icon size={14} strokeWidth={1.5} className="text-[var(--ink-muted)]" />}
      </div>
      {loading ? (
        <div className={`${small ? "h-7" : "h-9"} w-16 bg-[var(--warm-accent)]/60 rounded animate-pulse`} />
      ) : (
        <p className={`font-display ${small ? "text-2xl" : "text-3xl"} text-[var(--ink-primary)]`}>{value ?? 0}</p>
      )}
    </div>
  );
}

function StatusPill({ status, comp }) {
  if (comp) return <Pill color="ok">Comp</Pill>;
  const map = {
    trialing: "warn",
    active: "ok",
    past_due: "err",
    locked: "err",
    canceled: "muted",
    comped: "ok",
  };
  return <Pill color={map[status] || "muted"}>{status}</Pill>;
}

function Pill({ color, children }) {
  const colors = {
    ok: "text-[var(--ok)] bg-[var(--ok)]/10 border-[var(--ok)]/30",
    warn: "text-[var(--warn)] bg-[var(--warn)]/15 border-[var(--warn)]/40",
    err: "text-[var(--err)] bg-[var(--err)]/10 border-[var(--err)]/30",
    muted: "text-[var(--ink-muted)] bg-[var(--warm-accent)] border-[var(--line)]",
  };
  return <span className={`inline-block text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${colors[color]}`}>{children}</span>;
}

function formatDateShort(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch { return iso; }
}
